import { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, MapPin, Calendar as CalendarIcon, TrendingUp, Users, CheckSquare, Clock, Copy, ChevronRight, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getBaseUrl = () => {
  const url = BACKEND_URL || window.location.origin;
  return url.replace(/\/api\/?$/, '');
};

const StatCard = ({ title, value, icon: Icon, trend, testId, onClick }) => (
  <Card 
    data-testid={testId} 
    className={`bg-card border-border/50 hover:border-primary/50 transition-colors ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-bold font-heading mt-0.5 truncate">{value}</p>
          {trend && <p className="text-[10px] sm:text-xs text-muted-foreground">{trend}</p>}
        </div>
        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0 ml-2" />
      </div>
    </CardContent>
  </Card>
);

const AlertItem = ({ title, count, icon: Icon, actionPath, colorClass }) => {
  const navigate = useNavigate();
  
  if (count === 0) return null;
  
  return (
    <button
      onClick={() => navigate(actionPath)}
      className={`w-full flex items-center justify-between p-3 rounded-lg border-l-4 ${colorClass} bg-card hover:bg-secondary/30 transition-colors`}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold">{count}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('staff');
  const navigate = useNavigate();

  useEffect(() => {
    const user = localStorage.getItem('vyrus_user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role || 'staff');
      } catch (e) {}
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [analyticsRes, eventsRes, alertsRes, salesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/summary`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
        axios.get(`${BACKEND_URL}/api/dashboard/alerts`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/product-sales/analytics`, { headers }).catch(() => ({ data: null })),
      ]);

      setStats(analyticsRes.data);
      setAlerts(alertsRes.data);
      
      // Set top 3 sellers
      if (salesRes.data?.top_sellers) {
        setTopSellers(salesRes.data.top_sellers.slice(0, 3));
      }
      
      const upcoming = eventsRes.data
        .filter(e => new Date(e.start_date) >= new Date())
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
        .slice(0, 5);
      setUpcomingEvents(upcoming);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    // Parse date string directly without timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const copyCommissionLink = async () => {
    const link = `${getBaseUrl()}/request-commission`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Commission request link copied!');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Commission request link copied!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const hasAlerts = alerts && (alerts.pending_approvals > 0 || alerts.overdue_tasks > 0 || alerts.pending_commissions > 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">Welcome back to Vyrus</p>
        </div>
        <Button
          onClick={() => navigate('/events')}
          data-testid="add-event-button"
          size="sm"
          className="bg-primary text-primary-foreground shadow-[0_0_15px_rgba(34,211,238,0.3)] text-xs sm:text-sm"
        >
          <MapPin className="h-4 w-4 sm:mr-1" />
          <span className="hidden sm:inline">Add Event</span>
        </Button>
      </div>

      {/* Stats Grid - 2x2 on mobile */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <StatCard
          testId="stat-total-events"
          title="Total Events"
          value={stats?.total_events || 0}
          icon={MapPin}
          onClick={() => navigate('/events')}
        />
        <StatCard
          testId="stat-upcoming-events"
          title="Upcoming"
          value={stats?.upcoming_events || 0}
          icon={CalendarIcon}
          onClick={() => navigate('/calendar')}
        />
        {userRole === 'management' && (
          <>
            <StatCard
              testId="stat-total-revenue"
              title="Revenue"
              value={formatCurrency(stats?.total_revenue || 0)}
              icon={DollarSign}
              onClick={() => navigate('/analytics')}
            />
            <StatCard
              testId="stat-net-profit"
              title="Net Profit"
              value={formatCurrency(stats?.net_profit || 0)}
              icon={TrendingUp}
              trend={stats?.net_profit >= 0 ? 'Positive' : 'Negative'}
              onClick={() => navigate('/analytics')}
            />
          </>
        )}
      </div>

      {/* Alerts Section - Compact on mobile */}
      {userRole === 'management' && hasAlerts && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2 pt-3 px-3 sm:px-4">
            <CardTitle className="text-sm sm:text-base">Needs Attention</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 pt-0 space-y-2">
            <AlertItem
              title="Pending Approvals"
              count={alerts?.pending_approvals || 0}
              icon={Users}
              actionPath="/users"
              colorClass="border-l-yellow-500"
            />
            <AlertItem
              title="Overdue Tasks"
              count={alerts?.overdue_tasks || 0}
              icon={Clock}
              actionPath="/tasks"
              colorClass="border-l-red-500"
            />
            <AlertItem
              title="Pending Commissions"
              count={alerts?.pending_commissions || 0}
              icon={CheckSquare}
              actionPath="/commissions"
              colorClass="border-l-purple-500"
            />
          </CardContent>
        </Card>
      )}

      {/* Commission Link - Compact */}
      {userRole === 'management' && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-primary">Commission Link</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                  {getBaseUrl()}/request-commission
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={copyCommissionLink} className="flex-shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Sellers Widget */}
      {userRole === 'management' && topSellers.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2 pt-3 px-3 sm:px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                Top Sellers
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/product-sales')} className="text-xs">
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {topSellers.map((item, idx) => (
                <div 
                  key={item.name} 
                  className={`p-3 rounded-lg ${idx === 0 ? 'bg-primary/10 border border-primary/30' : 'bg-secondary/30'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm font-medium truncate flex-1">{item.name}</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground pl-8">
                    <span>{item.quantity} sold</span>
                    <span className="text-green-400 font-mono">{formatCurrency(item.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Events - Compact cards */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2 pt-3 px-3 sm:px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base sm:text-lg">Upcoming Events</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/events')} className="text-xs">
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-2 sm:p-4 pt-0">
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm" data-testid="no-upcoming-events">
              No upcoming events
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map((event) => (
                <button
                  key={event.id}
                  data-testid={`event-${event.id}`}
                  onClick={() => navigate(`/events/${event.id}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm sm:text-base truncate">{event.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{event.location}</p>
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-xs sm:text-sm font-medium">{formatDate(event.start_date)}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground capitalize">
                      {event.application_status.replace('_', ' ')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
